import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ErrorNotice } from '@bcecloud/portal-ui';
import { readMyIdentity } from '@/lib/events';
import {
  REP_CONNECTION_QUERY_KEY,
  REP_CONNECTION_STATUS,
  canWriteAsRep,
  connectSalesforcePath,
  isNotAllowed,
  readRepConnection,
  type RepConnection,
} from '@/lib/sales-workspace';

/**
 * The signed-in rep's Salesforce connection, shared by every sales workspace
 * screen (same cache as the profile's Connect Salesforce section). A 403
 * means the user is not on the allow-list: no retry.
 */
export function useRepConnection() {
  return useQuery({
    queryKey: REP_CONNECTION_QUERY_KEY,
    queryFn: ({ signal }) => readRepConnection(signal),
    retry: (count, error) => !isNotAllowed(error) && count < 2,
    staleTime: 60_000,
  });
}

/** Whether the rep may act now; actions stay disabled until this is true. */
export function useCanWriteAsRep(): boolean {
  return canWriteAsRep(useRepConnection().data);
}

function noticeText(connection: RepConnection): { readonly title: string; readonly message: string } {
  if (!connection.configured) {
    return {
      title: 'Changes are off.',
      message: 'Saving to Salesforce is not set up on this server yet, so the actions here are turned off.',
    };
  }
  if (connection.status === REP_CONNECTION_STATUS.needsReconnect) {
    return {
      title: 'Reconnect Salesforce.',
      message: 'Salesforce no longer accepts your saved login. Reconnect it on your profile to make changes here.',
    };
  }
  return {
    title: 'Connect Salesforce to make changes.',
    message: 'Changes here are saved in Salesforce under your own login. Connect it once on your profile.',
  };
}

/**
 * The banner every sales workspace screen shows while the rep cannot write:
 * why, and a link to the Connect Salesforce section on their own profile.
 *
 * @returns The notice, or null when the rep is connected (or still loading).
 */
export function RepConnectionNotice(): React.ReactElement | null {
  const connection = useRepConnection();
  const me = useQuery({ queryKey: ['staff-me'], queryFn: ({ signal }) => readMyIdentity(signal) });
  if (!connection.data || canWriteAsRep(connection.data)) return null;
  const { title, message } = noticeText(connection.data);
  return (
    <div className="mb-4">
      <ErrorNotice
        inline
        tone="warning"
        title={title}
        message={message}
        action={
          me.data && connection.data.configured ? (
            <Link to={connectSalesforcePath(me.data.id)} className="text-portal-small font-bold text-brand hover:underline">
              {connection.data.status === REP_CONNECTION_STATUS.needsReconnect ? 'Reconnect Salesforce' : 'Connect Salesforce'}
            </Link>
          ) : undefined
        }
      />
    </div>
  );
}
